import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { Activity, AlertCircle, CheckCircle, Database, WifiOff, Info, Clock, RefreshCw, ExternalLink, Search, Download } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell } from "recharts";
import StatCard from "../components/StatCard";
import { SectionHeader, PageHeader } from "../components/SectionHeader";
import { useFilters } from "../context/FilterContext";
import RAGBadge from "../components/RAGBadge";
import { DISTRICT_MAPPING_LOG, BLOCK_MAPPING_LOG } from "../data/geoMappings";
import ThemeChip from "../components/ThemeChip";
import InfoTooltip from "../components/InfoTooltip";
import { fmtDate, fmtNum, fmtCr, displayTheme } from "../utils/format";

const SEV_COLOR = { red: "bg-red-100 text-red-700", amber: "bg-amber-100 text-amber-700", green: "bg-green-100 text-green-700" };
const METHOD_COLOR = { "Block-proportional": "bg-emerald-100 text-emerald-700", "District-proportional": "bg-blue-100 text-blue-700", "Equal split": "bg-slate-100 text-slate-600" };

export default function DataHealth() {
  const { filterOptions, API } = useFilters();
  const [summary, setSummary] = useState(null);
  const [completeness, setCompleteness] = useState([]);
  const [issues, setIssues] = useState([]);
  const [nullPid, setNullPid] = useState(null);
  const [loading, setLoading] = useState(true);
  const sf = filterOptions.data_sources?.fluxx || filterOptions.snowflake_connected;
  const [multiState, setMultiState] = useState(null);
  const [msLoading, setMsLoading] = useState(false);
  const [msPage, setMsPage] = useState(1);
  const [msFilter, setMsFilter] = useState("");
  const [kpiListing, setKpiListing] = useState(null);
  const [kpiPage, setKpiPage] = useState(1);
  const [kpiSearch, setKpiSearch] = useState("");
  const [gapSummary, setGapSummary] = useState([]);
  const [syncHistory, setSyncHistory] = useState([]);
  const [lastSync, setLastSync] = useState(null);
  const [aspirationalLists, setAspirationalLists] = useState(null);
  const [adSearch, setAdSearch] = useState("");
  const [abSearch, setAbSearch] = useState("");
  const [validationReport, setValidationReport] = useState(null);
  const [validationLoading, setValidationLoading] = useState(false);
  const [geoBreakdown, setGeoBreakdown] = useState(null);
  const [dedupLog, setDedupLog] = useState([]);
  const [earlyPayments, setEarlyPayments] = useState(null);
  const [epSearch, setEpSearch] = useState("");

  // Generic CSV export helper
  const exportToCSV = (data, filename, columns) => {
    if (!data || data.length === 0) return;
    const headers = columns.map(c => c.label).join(',');
    const rows = data.map(row => 
      columns.map(c => {
        const val = c.getValue ? c.getValue(row) : row[c.key];
        // Escape quotes and wrap in quotes if contains comma
        const strVal = String(val ?? '');
        return strVal.includes(',') || strVal.includes('"') 
          ? `"${strVal.replace(/"/g, '""')}"` 
          : strVal;
      }).join(',')
    );
    const csv = [headers, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}_${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/data-health/summary`),
      axios.get(`${API}/data-health/completeness`),
      axios.get(`${API}/data-health/issues`),
      axios.get(`${API}/data-health/null-program-id`),
      axios.get(`${API}/data-health/gap-summary`).catch(() => ({ data: [] })),
      axios.get(`${API}/data-health/sync-history`).catch(() => ({ data: [] })),
      axios.get(`${API}/data-health/last-sync`).catch(() => ({ data: null })),
      axios.get(`${API}/data-health/dedup-log`).catch(() => ({ data: [] })),
    ]).then(([sRes, cRes, iRes, nRes, gRes, shRes, lsRes, dlRes]) => {
      setSummary(sRes.data);
      setCompleteness(cRes.data);
      setIssues(iRes.data);
      setNullPid(nRes.data);
      setGapSummary(gRes.data);
      setSyncHistory(shRes.data);
      setLastSync(lsRes.data);
      setDedupLog(dlRes.data || []);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!sf) return;
    setMsLoading(true);
    axios.get(`${API}/data-health/multi-state`, { params: { page: msPage, per_page: 30, search: msFilter || undefined } })
      .then(r => setMultiState(r.data))
      .catch(console.error)
      .finally(() => setMsLoading(false));
  }, [sf, msPage, msFilter]);

  useEffect(() => {
    if (!sf) return;
    axios.get(`${API}/data-health/grant-kpi-listing`, {
      params: { page: kpiPage, per_page: 20, search: kpiSearch || undefined }
    }).then(r => setKpiListing(r.data)).catch(console.error);
  }, [sf, kpiPage, kpiSearch]);

  useEffect(() => {
    axios.get(`${API}/geographic/aspirational-lists`)
      .then(r => setAspirationalLists(r.data))
      .catch(console.error);
  }, [API]);

  // Fetch data validation report
  useEffect(() => {
    setValidationLoading(true);
    axios.get(`${API}/data-health/validation-report`)
      .then(r => setValidationReport(r.data))
      .catch(console.error)
      .finally(() => setValidationLoading(false));
  }, [API]);

  // Fetch early payments (paid before project start date)
  useEffect(() => {
    axios.get(`${API}/data-health/early-payments`)
      .then(r => setEarlyPayments(r.data))
      .catch(console.error);
  }, [API]);

  // Fetch geography breakdown
  useEffect(() => {
    axios.get(`${API}/data-health/geography-breakdown`)
      .then(r => setGeoBreakdown(r.data))
      .catch(console.error);
  }, [API]);

  // Filtered aspirational lists for search (supports filtering by grant coverage status)
  const filteredDistricts = useMemo(() => {
    const s = adSearch.toLowerCase().trim();
    if (!s || !aspirationalLists) return aspirationalLists?.districts || [];
    // Support filtering by grant coverage: "yes", "no", "covered", "uncovered", "✓", "✗"
    const coverageYes = ["yes", "covered", "✓", "has grant", "hasgrant"].includes(s);
    const coverageNo = ["no", "uncovered", "✗", "no grant", "nogrant"].includes(s);
    if (coverageYes) {
      return aspirationalLists.districts.filter(d => d.has_grant === true);
    }
    if (coverageNo) {
      return aspirationalLists.districts.filter(d => d.has_grant === false);
    }
    return aspirationalLists.districts.filter(d =>
      d.district.toLowerCase().includes(s) || d.state.toLowerCase().includes(s)
    );
  }, [aspirationalLists, adSearch]);

  const filteredBlocks = useMemo(() => {
    const s = abSearch.toLowerCase().trim();
    if (!s || !aspirationalLists) return aspirationalLists?.blocks || [];
    // Support filtering by grant coverage: "yes", "no", "covered", "uncovered", "✓", "✗"
    const coverageYes = ["yes", "covered", "✓", "has grant", "hasgrant"].includes(s);
    const coverageNo = ["no", "uncovered", "✗", "no grant", "nogrant"].includes(s);
    if (coverageYes) {
      return aspirationalLists.blocks.filter(b => b.has_grant === true);
    }
    if (coverageNo) {
      return aspirationalLists.blocks.filter(b => b.has_grant === false);
    }
    return aspirationalLists.blocks.filter(b =>
      b.block.toLowerCase().includes(s) || b.district.toLowerCase().includes(s) || b.state.toLowerCase().includes(s)
    );
  }, [aspirationalLists, abSearch]);

  return (
    <div data-testid="data-health-page" className="space-y-6">
      <PageHeader title="Data Health" subtitle="Cross-source quality diagnostics and MIS compliance" badge="07" />

      {/* MANDATORY: NULL PROGRAM_ID Banner */}
      <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-5" data-testid="null-program-id-banner">
        <div className="flex items-start gap-3">
          <AlertCircle size={22} className="text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="text-sm font-bold text-amber-900 mb-1">
              20,031 MEAS Records Excluded — NULL PROGRAM_ID
            </div>
            <div className="text-xs text-amber-800 space-y-1">
              <p><strong>20,031 MEAS records (28.5% of 70,178 total)</strong> have no PROGRAM_ID and cannot be linked to any grant in FLUXX.</p>
              <p>These records are <strong>EXCLUDED</strong> from all dashboard counts, KPI calculations, achievement scores, RAG ratings, and financial figures across all 7 pages.</p>
              <p><strong>Action required:</strong> TT data team to investigate source of NULL PROGRAM_ID in V_THEMATIC_PROGRAM_MEASUREMENTS and backfill where possible.</p>
            </div>
            <div className="flex gap-4 mt-3">
              <div className="bg-white rounded-lg px-3 py-2 text-center">
                <div className="text-lg font-bold text-amber-800">20,031</div>
                <div className="text-xs text-amber-600">Excluded rows</div>
              </div>
              <div className="bg-white rounded-lg px-3 py-2 text-center">
                <div className="text-lg font-bold text-amber-800">28.5%</div>
                <div className="text-xs text-amber-600">Of total MEAS</div>
              </div>
              <div className="bg-white rounded-lg px-3 py-2 text-center">
                <div className="text-lg font-bold text-amber-800">49+</div>
                <div className="text-xs text-amber-600">Affected grants (est.)</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Hero Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard title="Overall Health Score" value={summary?.overall_score ? `${summary.overall_score}%` : null}
          icon={Activity} color="red" loading={loading}
          tooltip="Data Health Score = KPI assignment (30%) + Baseline/Achievement (25%) + PO Assignment (20%) + Geo Tagging (15%) + MIS Compliance (10%)" />
        <StatCard title="Payments Loaded" value={summary?.payments_rows} icon={Database} color="green" loading={loading} />
        <StatCard title="Budget Records" value={summary?.budget_rows} icon={Database} color="blue" loading={loading} />
        <StatCard title="Utilization Records" value={summary?.utilization_rows} icon={Database} color="amber" loading={loading} />
      </div>

      {/* Completeness Metrics */}
      <SectionHeader title="Data Completeness" subtitle="Coverage metrics across all data sources">
        <div className="space-y-4">
          {completeness.map((r, i) => (
            <div key={i} className="flex items-center gap-4">
              <div className="text-xs text-slate-600 w-64 flex-shrink-0">{r.metric}</div>
              <div className="flex-1 bg-slate-100 rounded-full h-2.5">
                <div className={`h-2.5 rounded-full transition-all ${r.pct >= 80 ? "bg-green-500" : r.pct >= 50 ? "bg-amber-400" : "bg-red-500"}`}
                  style={{ width: `${r.pct}%` }} />
              </div>
              <div className="text-xs font-medium text-slate-700 w-32 text-right">
                {r.pct}% ({r.complete?.toLocaleString()} / {r.total?.toLocaleString()})
              </div>
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${r.pct >= 80 ? "bg-green-100 text-green-700" : r.pct >= 50 ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}`}>
                {r.pct >= 80 ? "Good" : r.pct >= 50 ? "Partial" : "Low"}
              </span>
            </div>
          ))}
        </div>
      </SectionHeader>

      {/* Data Quality Issues */}
      <SectionHeader title="Data Quality Issues" subtitle="Action items requiring attention">
        <div className="space-y-3" data-testid="data-quality-issues">
          {issues.map((issue, i) => (
            <div key={i} className={`rounded-lg p-4 border ${issue.severity === "red" ? "bg-red-50 border-red-200" : issue.severity === "amber" ? "bg-amber-50 border-amber-200" : "bg-green-50 border-green-200"}`}>
              <div className="flex items-start gap-3">
                <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${issue.severity === "red" ? "bg-red-500" : issue.severity === "amber" ? "bg-amber-400" : "bg-green-500"}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-slate-900">{issue.issue}</span>
                    <span className="text-xs bg-white border border-slate-200 px-2 py-0.5 rounded-full text-slate-500">{issue.source}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${SEV_COLOR[issue.severity] || SEV_COLOR.amber}`}>
                      {issue.affected_records?.toLocaleString()} records
                    </span>
                  </div>
                  <div className="text-xs text-slate-500 mt-1">{issue.action}</div>
                </div>
              </div>
            </div>
          ))}
          {issues.length === 0 && <div className="text-sm text-slate-600 text-center py-4">No critical issues found</div>}
        </div>
      </SectionHeader>

      {/* Snowflake Sync History */}
      <SectionHeader 
        title="Snowflake Sync History" 
        subtitle={lastSync?.last_sync_at ? `Last synced ${lastSync.hours_ago < 1 ? 'just now' : lastSync.hours_ago < 48 ? `${lastSync.hours_ago}h ago` : `${lastSync.days_ago} days ago`}` : "No sync data available"}
        collapsible 
        defaultOpen={true}
      >
        <div className="mb-4 flex items-center gap-3">
          <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${
            lastSync?.hours_ago != null && lastSync.hours_ago < 48 
              ? "bg-green-50 border border-green-200 text-green-800"
              : lastSync?.days_ago != null && lastSync.days_ago < 4
              ? "bg-amber-50 border border-amber-200 text-amber-800"
              : "bg-slate-50 border border-slate-200 text-slate-600"
          }`}>
            <Clock size={14} />
            {lastSync?.last_sync_at ? (
              <span>
                <strong>Last Sync:</strong> {new Date(lastSync.last_sync_at).toLocaleString("en-IN", {
                  day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit"
                })}
              </span>
            ) : (
              <span>No sync history available</span>
            )}
          </div>
          <div className="text-xs text-slate-600 flex items-center gap-1">
            <RefreshCw size={11} />
            Auto-syncs every 48 hours
          </div>
        </div>
        
        {syncHistory.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="sync-history-table">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  <th className="text-left font-medium text-slate-500 uppercase py-2 px-3">Sync Time</th>
                  <th className="text-right font-medium text-slate-500 uppercase py-2 px-3">FLUXX Grants</th>
                  <th className="text-right font-medium text-slate-500 uppercase py-2 px-3">Payments</th>
                  <th className="text-right font-medium text-slate-500 uppercase py-2 px-3">Budget</th>
                  <th className="text-right font-medium text-slate-500 uppercase py-2 px-3">Utilization</th>
                  <th className="text-right font-medium text-slate-500 uppercase py-2 px-3">MEAS KPIs</th>
                  <th className="text-center font-medium text-slate-500 uppercase py-2 px-3">Changes</th>
                </tr>
              </thead>
              <tbody>
                {syncHistory.map((session, i) => {
                  const t = session.tables;
                  const d = session.delta_vs_oldest || {};
                  const hasChanges = Object.values(d).some(v => v !== 0);
                  return (
                    <tr key={session.session_id} className={`border-b border-slate-50 ${i === 0 ? "bg-green-50/30" : "hover:bg-slate-50"}`}>
                      <td className="py-2 px-3 text-slate-700">
                        {new Date(session.synced_at).toLocaleString("en-IN", {
                          day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit"
                        })}
                        {i === 0 && <span className="ml-2 text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded">Latest</span>}
                      </td>
                      <td className="py-2 px-3 text-right font-medium">{(t.fluxx_grants || 0).toLocaleString()}</td>
                      <td className="py-2 px-3 text-right font-medium">{(t.payments || 0).toLocaleString()}</td>
                      <td className="py-2 px-3 text-right font-medium">{(t.budget_details || 0).toLocaleString()}</td>
                      <td className="py-2 px-3 text-right font-medium">{(t.utilization_details || 0).toLocaleString()}</td>
                      <td className="py-2 px-3 text-right font-medium">{(t.meas_indicators || 0).toLocaleString()}</td>
                      <td className="py-2 px-3 text-center">
                        {hasChanges ? (
                          <span className="text-amber-600 font-medium">
                            {Object.entries(d).filter(([,v]) => v !== 0).map(([k,v]) => 
                              `${k.replace('_details','').replace('_indicators','')}: ${v > 0 ? '+' : ''}${v}`
                            ).join(', ')}
                          </span>
                        ) : (
                          <span className="text-slate-600">No changes</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8 text-slate-600">
            <RefreshCw size={24} className="mx-auto mb-2 opacity-50" />
            <p>No sync history available yet. Data will appear after the first Snowflake sync.</p>
          </div>
        )}
      </SectionHeader>


      {/* Deduplication History Log */}
      <SectionHeader
        title="Deduplication Log"
        subtitle="History of duplicate rows detected and removed from Snowflake source data during ETL sync"
        collapsible
        defaultOpen={false}
      >
        {dedupLog.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="dedup-log-table">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  <th className="text-left font-medium text-slate-500 uppercase py-2 px-3">Sync Time</th>
                  <th className="text-left font-medium text-slate-500 uppercase py-2 px-3">Table</th>
                  <th className="text-right font-medium text-slate-500 uppercase py-2 px-3">Source Rows</th>
                  <th className="text-right font-medium text-slate-500 uppercase py-2 px-3">After Cleanup</th>
                  <th className="text-right font-medium text-slate-500 uppercase py-2 px-3">Duplicates Removed</th>
                  <th className="text-right font-medium text-slate-500 uppercase py-2 px-3">Duplicate Rate</th>
                </tr>
              </thead>
              <tbody>
                {dedupLog.map((row, i) => (
                  <tr key={i} className="border-b border-slate-50 hover:bg-slate-50">
                    <td className="py-2 px-3 text-slate-600">
                      {row.logged_at ? new Date(row.logged_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—"}
                      {row.session_id && <span className="ml-1 text-slate-600 font-mono text-[10px]">#{row.session_id}</span>}
                    </td>
                    <td className="py-2 px-3 font-medium text-slate-700">{row.table_name}</td>
                    <td className="py-2 px-3 text-right text-slate-600">{(row.rows_before || 0).toLocaleString()}</td>
                    <td className="py-2 px-3 text-right text-slate-600">{(row.rows_after || 0).toLocaleString()}</td>
                    <td className="py-2 px-3 text-right font-medium text-red-600">{(row.duplicates_removed || 0).toLocaleString()}</td>
                    <td className="py-2 px-3 text-right">
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${row.duplicate_rate > 30 ? "bg-red-100 text-red-700" : row.duplicate_rate > 10 ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600"}`}>
                        {row.duplicate_rate}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8 text-slate-600">
            <Database size={24} className="mx-auto mb-2 opacity-50" />
            <p>No deduplication events recorded yet. Log will populate after the next ETL sync.</p>
          </div>
        )}
      </SectionHeader>

      {/* Data Source Status */}
      <div className={`rounded-xl p-5 border-2 ${filterOptions.snowflake_connected ? "bg-green-50 border-green-200" : sf ? "bg-blue-50 border-blue-200" : "bg-red-50 border-red-200"}`} data-testid="snowflake-status-card">
        <div className="flex items-center gap-3">
          {filterOptions.snowflake_connected ? <CheckCircle size={22} className="text-green-600" /> : sf ? <CheckCircle size={22} className="text-blue-600" /> : <WifiOff size={22} className="text-red-500" />}
          <div>
            <div className={`text-sm font-semibold ${filterOptions.snowflake_connected ? "text-green-800" : sf ? "text-blue-800" : "text-red-800"}`}>
              {filterOptions.snowflake_connected ? "Snowflake: Connected" : sf ? "Using cached data (Snowflake offline)" : "Snowflake: Disconnected — No data available"}
            </div>
            {!filterOptions.snowflake_connected && filterOptions.snowflake_error && (
              <div className="text-xs text-slate-500 mt-0.5 font-mono truncate max-w-lg">{filterOptions.snowflake_error.slice(0, 150)}</div>
            )}
            {!filterOptions.snowflake_connected && sf && (
              <div className="text-xs text-blue-600 mt-1">
                Using cached data from the last Snowflake sync. Click "Refresh Data" to retry Snowflake.
              </div>
            )}
            {!sf && (
              <div className="text-xs text-red-600 mt-1">
                Fix credentials in backend/.env (SNOWFLAKE_PASSWORD) and click "Refresh Data" in sidebar.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── District Name Mapping Registry ──────────────────────────────── */}
      <SectionHeader
        title="Geographic Name Mapping Registry"
        subtitle="How FLUXX district names map to the geoBoundaries map source — reviewed for transparency"
        collapsible
        defaultOpen={false}
      >
        <div className="mb-3 flex flex-wrap gap-2 text-xs">
          {[
            { type: "spelling", label: "Alternate Spelling", color: "bg-blue-100 text-blue-700" },
            { type: "renamed", label: "Renamed", color: "bg-purple-100 text-purple-700" },
            { type: "new_district", label: "New District → Parent", color: "bg-amber-100 text-amber-700" },
            { type: "absent", label: "Absent from Map", color: "bg-red-100 text-red-700" },
          ].map(({ type, label, color }) => (
            <span key={type} className={`px-2 py-0.5 rounded-full text-xs font-medium ${color}`}>{label}</span>
          ))}
        </div>
        <div className="overflow-x-auto max-h-96 overflow-y-auto rounded-lg border border-slate-100">
          <table className="w-full text-xs" data-testid="mapping-registry-table">
            <thead className="sticky top-0 bg-slate-50">
              <tr className="border-b border-slate-200">
                <th className="text-left font-medium text-slate-500 uppercase py-2 px-3 w-40">DB Name (FLUXX)</th>
                <th className="text-left font-medium text-slate-500 uppercase py-2 px-3 w-40">Map Source Name</th>
                <th className="text-left font-medium text-slate-500 uppercase py-2 px-3">Reason</th>
                <th className="text-left font-medium text-slate-500 uppercase py-2 px-3 w-28">Type</th>
              </tr>
            </thead>
            <tbody>
              {DISTRICT_MAPPING_LOG.map((row, i) => {
                const typeBadge = {
                  spelling:     "bg-blue-50 text-blue-700",
                  renamed:      "bg-purple-50 text-purple-700",
                  new_district: "bg-amber-50 text-amber-700",
                  absent:       "bg-red-50 text-red-700",
                }[row.type] || "bg-slate-100 text-slate-600";

                return (
                  <tr key={i} className={`border-b border-slate-50 ${i % 2 === 0 ? "bg-white" : "bg-slate-50/50"}`}>
                    <td className="py-2 px-3 font-medium text-slate-800">{row.db}</td>
                    <td className="py-2 px-3 text-slate-600">
                      {row.map ? (
                        <span>{row.map}</span>
                      ) : (
                        <span className="text-red-500 italic">— not in map —</span>
                      )}
                    </td>
                    <td className="py-2 px-3 text-slate-500">{row.reason}</td>
                    <td className="py-2 px-3">
                      <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${typeBadge}`}>
                        {row.type.replace("_", " ")}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="mt-2 text-xs text-slate-600">
          {DISTRICT_MAPPING_LOG.filter(r => r.type === "absent").length} districts absent from map source ·{" "}
          {DISTRICT_MAPPING_LOG.filter(r => r.type === "new_district").length} new districts mapped to parent ·{" "}
          {DISTRICT_MAPPING_LOG.filter(r => r.type === "renamed").length} renamed districts ·{" "}
          {DISTRICT_MAPPING_LOG.filter(r => r.type === "spelling").length} spelling variants
        </div>
      </SectionHeader>

      {/* ── Block Name Mapping Registry ──────────────────────────────── */}
      <SectionHeader
        title="Block Name Mapping Registry"
        subtitle="How FLUXX/grant-geography block names map to the GeoJSON block source — ensures map highlights are correct"
        collapsible
        defaultOpen={false}
      >
        <div className="mb-3 flex flex-wrap gap-2 text-xs">
          {[
            { type: "spelling", label: "Alternate Spelling", color: "bg-blue-100 text-blue-700" },
            { type: "format",   label: "Format Difference",  color: "bg-teal-100 text-teal-700"  },
            { type: "absent",   label: "Absent from GeoJSON", color: "bg-red-100 text-red-700"   },
          ].map(({ type, label, color }) => (
            <span key={type} className={`px-2 py-0.5 rounded-full text-xs font-medium ${color}`}>{label}</span>
          ))}
        </div>
        <div className="overflow-x-auto max-h-96 overflow-y-auto rounded-lg border border-slate-100">
          <table className="w-full text-xs" data-testid="block-mapping-registry-table">
            <thead className="sticky top-0 bg-slate-50">
              <tr className="border-b border-slate-200">
                <th className="text-left font-medium text-slate-500 uppercase py-2 px-3 w-44">DB Name (FLUXX)</th>
                <th className="text-left font-medium text-slate-500 uppercase py-2 px-3 w-44">GeoJSON Block Name</th>
                <th className="text-left font-medium text-slate-500 uppercase py-2 px-3 w-32">State</th>
                <th className="text-left font-medium text-slate-500 uppercase py-2 px-3">Reason</th>
                <th className="text-left font-medium text-slate-500 uppercase py-2 px-3 w-24">Type</th>
              </tr>
            </thead>
            <tbody>
              {BLOCK_MAPPING_LOG.map((row, i) => {
                const typeBadge = {
                  spelling: "bg-blue-50 text-blue-700",
                  format:   "bg-teal-50 text-teal-700",
                  absent:   "bg-red-50 text-red-600",
                }[row.type] || "bg-slate-100 text-slate-600";

                return (
                  <tr key={i} className={`border-b border-slate-50 ${i % 2 === 0 ? "bg-white" : "bg-slate-50/50"}`}>
                    <td className="py-2 px-3 font-medium text-slate-800">{row.db}</td>
                    <td className="py-2 px-3 text-slate-600">{row.geo ?? <span className="text-red-400 italic">absent</span>}</td>
                    <td className="py-2 px-3 text-slate-500">{row.state}</td>
                    <td className="py-2 px-3 text-slate-500">{row.reason}</td>
                    <td className="py-2 px-3">
                      <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${typeBadge}`}>
                        {row.type}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="mt-2 text-xs text-slate-600">
          {BLOCK_MAPPING_LOG.filter(r => r.type === "spelling").length} spelling/transliteration variants ·{" "}
          {BLOCK_MAPPING_LOG.filter(r => r.type === "format").length} format differences (hyphen/space, compact/expanded, annotation removed) ·{" "}
          {BLOCK_MAPPING_LOG.filter(r => r.type === "absent").length} blocks absent from GeoJSON source ·{" "}
          {BLOCK_MAPPING_LOG.length} total entries
        </div>
      </SectionHeader>

      {/* Grant KPI Listing */}
      <SectionHeader
        title="Grant KPI Listing"
        subtitle={kpiListing ? `${kpiListing.total} grants — age, KPI count, achievement, staleness` : "Loading…"}
        collapsible defaultOpen={true}
        headerRight={
          <button
            onClick={() => {
              // Fetch all data for export
              axios.get(`${API}/data-health/grant-kpi-listing`, { params: { page: 1, per_page: 10000 } })
                .then(r => exportToCSV(r.data.data, 'grant_kpi_listing', [
                  { label: 'Grant ID', key: 'grant_id' },
                  { label: 'Grantee', key: 'grantee_name' },
                  { label: 'Theme', key: 'theme' },
                  { label: 'Status', key: 'status' },
                  { label: 'Age (Days)', key: 'grant_age_days' },
                  { label: 'KPI Count', key: 'kpi_count' },
                  { label: 'Avg Achievement %', key: 'avg_achievement' },
                  { label: 'On Track %', key: 'on_track_pct' },
                  { label: 'Last KPI FY', key: 'last_kpi_fy' },
                ]));
            }}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 transition-all"
          >
            <Download size={12} /> Export CSV
          </button>
        }
      >
        <div className="flex gap-2 mb-3">
          <input type="text" placeholder="Search by grant ID or grantee…" value={kpiSearch}
            onChange={e => { setKpiSearch(e.target.value); setKpiPage(1); }}
            className="text-xs border border-slate-200 rounded-lg px-3 py-1.5 flex-1 focus:outline-none focus:ring-1 focus:ring-[#B71C1C]"
            data-testid="kpi-listing-search" />
        </div>
        <div className="overflow-x-auto -mx-5 px-5 sm:mx-0 sm:px-0">
          <table className="w-full text-xs min-w-[900px]" data-testid="grant-kpi-listing-table">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50">
                <th className="text-left font-medium text-slate-500 uppercase py-2 px-2 whitespace-nowrap">Grant ID</th>
                <th className="text-left font-medium text-slate-500 uppercase py-2 whitespace-nowrap">Grantee</th>
                <th className="text-left font-medium text-slate-500 uppercase py-2 whitespace-nowrap">Theme</th>
                <th className="text-left font-medium text-slate-500 uppercase py-2 whitespace-nowrap">Status</th>
                <th className="text-right font-medium text-slate-500 uppercase py-2 whitespace-nowrap">
                  Age (Days)
                  <InfoTooltip text="Age of Grant = Days from Project Start Date to today." />
                </th>
                <th className="text-right font-medium text-slate-500 uppercase py-2 whitespace-nowrap">KPI Count</th>
                <th className="text-right font-medium text-slate-500 uppercase py-2 whitespace-nowrap">
                  Avg Achievement
                  <InfoTooltip text="Average KPI Achievement % = mean of (Actual ÷ Target × 100) across all MEAS records for this grant." />
                </th>
                <th className="text-right font-medium text-slate-500 uppercase py-2 whitespace-nowrap">On Track %</th>
                <th className="text-right font-medium text-slate-500 uppercase py-2 whitespace-nowrap">Last KPI FY</th>
              </tr>
            </thead>
            <tbody>
              {(kpiListing?.data || []).map((r, i) => (
                <tr key={i} className="border-b border-slate-50 hover:bg-slate-50 transition-colors">
                  <td className="py-2 px-2 font-mono text-slate-600 whitespace-nowrap">{r.grant_id}</td>
                  <td className="py-2 max-w-[160px] truncate text-slate-700">{r.grantee}</td>
                  <td className="py-2"><ThemeChip theme={r.theme} /></td>
                  <td className="py-2">
                    <span className={`px-1.5 py-0.5 rounded text-xs ${r.status === 'Active' || r.status === 'Ongoing' ? "bg-green-100 text-green-700" : r.status === 'Closed' ? "bg-slate-100 text-slate-500" : "bg-amber-100 text-amber-700"}`}>
                      {r.status || "–"}
                    </span>
                  </td>
                  <td className="py-2 text-right">
                    {r.age_of_grant_days != null ? (
                      <span className={r.age_of_grant_days > 1825 ? "text-amber-600 font-medium" : "text-slate-600"}>
                        {r.age_of_grant_days.toLocaleString()}
                      </span>
                    ) : "–"}
                  </td>
                  <td className="py-2 text-right text-slate-600">{r.kpi_count || "–"}</td>
                  <td className="py-2 text-right">
                    {r.avg_achievement_pct != null ? (
                      <span className={`px-1.5 py-0.5 rounded ${r.avg_achievement_pct >= 80 ? "bg-green-100 text-green-700" : r.avg_achievement_pct >= 50 ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}`}>
                        {r.avg_achievement_pct}%
                      </span>
                    ) : <span className="text-slate-500">No KPI data</span>}
                  </td>
                  <td className="py-2 text-right">
                    {r.on_track_pct != null ? (
                      <span className={r.on_track_pct >= 60 ? "text-green-600 font-medium" : "text-red-600 font-medium"}>
                        {r.on_track_pct}%
                      </span>
                    ) : "–"}
                  </td>
                  <td className="py-2 text-right text-slate-500">{r.last_kpi_fy || <span className="text-slate-500">No data</span>}</td>
                </tr>
              ))}
              {!kpiListing && (
                <tr><td colSpan={9} className="text-center py-8 text-slate-600">Loading grant KPI data…</td></tr>
              )}
            </tbody>
          </table>
        </div>
        {kpiListing && kpiListing.total > 20 && (
          <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100">
            <span className="text-xs text-slate-600">Showing {((kpiPage-1)*20)+1}–{Math.min(kpiPage*20, kpiListing.total)} of {kpiListing.total} grants</span>
            <div className="flex gap-2">
              <button onClick={() => setKpiPage(p => Math.max(1, p-1))} disabled={kpiPage === 1}
                className="text-xs px-3 py-1.5 bg-slate-100 rounded-lg disabled:opacity-40 hover:bg-slate-200 transition-colors">Prev</button>
              <button onClick={() => setKpiPage(p => p+1)} disabled={kpiPage * 20 >= kpiListing.total}
                className="text-xs px-3 py-1.5 bg-slate-100 rounded-lg disabled:opacity-40 hover:bg-slate-200 transition-colors">Next</button>
            </div>
          </div>
        )}
      </SectionHeader>

      {/* ── Grant Geography Breakdown ──────────────────────────────── */}
      <SectionHeader
        title="Grant Geography Breakdown"
        subtitle={geoBreakdown ? `${geoBreakdown.total_grants} total grants — distribution by geography type` : "Loading…"}
        data-testid="geo-breakdown-section"
      >
        {geoBreakdown ? (
          <div className="space-y-4">
            {/* Breakdown cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {geoBreakdown.breakdown.map((b) => {
                const colors = {
                  green: "bg-green-50 border-green-200 text-green-800",
                  blue: "bg-blue-50 border-blue-200 text-blue-800",
                  amber: "bg-amber-50 border-amber-200 text-amber-800",
                  red: "bg-red-50 border-red-200 text-red-800",
                };
                const barColors = { green: "bg-green-500", blue: "bg-blue-500", amber: "bg-amber-400", red: "bg-red-500" };
                const pct = geoBreakdown.total_grants > 0 ? Math.round(b.count / geoBreakdown.total_grants * 100) : 0;
                return (
                  <div key={b.category} className={`rounded-lg border p-3 ${colors[b.color]}`} data-testid={`geo-breakdown-${b.category.toLowerCase().replace(/\s/g, '-')}`}>
                    <div className="text-2xl font-bold">{b.count}</div>
                    <div className="text-xs font-medium mt-0.5">{b.category}</div>
                    <div className="mt-2 h-1.5 bg-white/50 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full ${barColors[b.color]}`} style={{ width: `${pct}%` }} />
                    </div>
                    <div className="text-[10px] mt-1 opacity-70">{pct}% of {geoBreakdown.total_grants} grants</div>
                  </div>
                );
              })}
            </div>

            {/* Flagged: grants with no geography */}
            {geoBreakdown.no_geography_grants.length > 0 && (
              <div className="border-2 border-red-200 bg-red-50/50 rounded-xl p-4" data-testid="no-geography-grants">
                <div className="flex items-center gap-2 mb-3">
                  <AlertCircle size={16} className="text-red-600" />
                  <span className="text-sm font-semibold text-red-800">
                    {geoBreakdown.no_geography_grants.length} Grants with No Geography Data
                  </span>
                  <span className="text-xs text-red-600 ml-auto">These grants are excluded from all map views</span>
                </div>
                <div className="overflow-x-auto rounded-lg border border-red-200">
                  <table className="w-full text-xs" data-testid="no-geo-grants-table">
                    <thead className="bg-red-100/60">
                      <tr>
                        <th className="text-left font-medium text-red-700 uppercase py-2 px-3">Grant ID</th>
                        <th className="text-left font-medium text-red-700 uppercase py-2 px-3">Project Title</th>
                        <th className="text-left font-medium text-red-700 uppercase py-2 px-3">Grantee</th>
                        <th className="text-left font-medium text-red-700 uppercase py-2 px-3">Theme</th>
                        <th className="text-left font-medium text-red-700 uppercase py-2 px-3">Status</th>
                        <th className="text-left font-medium text-red-700 uppercase py-2 px-3">FY</th>
                        <th className="text-right font-medium text-red-700 uppercase py-2 px-3">Sanctioned</th>
                        <th className="text-left font-medium text-red-700 uppercase py-2 px-3">Raw Geography Field</th>
                      </tr>
                    </thead>
                    <tbody>
                      {geoBreakdown.no_geography_grants.map((g, i) => (
                        <tr key={i} className="border-t border-red-100 hover:bg-red-50 transition-colors">
                          <td className="py-2 px-3 font-mono text-slate-700">{g.grant_id}</td>
                          <td className="py-2 px-3 text-slate-700 max-w-[200px] truncate" title={g.project_title}>{g.project_title || "–"}</td>
                          <td className="py-2 px-3 text-slate-600">{g.grantee || "–"}</td>
                          <td className="py-2 px-3">{g.theme ? <ThemeChip theme={g.theme} /> : "–"}</td>
                          <td className="py-2 px-3">
                            <span className={`px-1.5 py-0.5 rounded text-xs ${g.status === 'Ongoing' ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"}`}>
                              {g.status || "–"}
                            </span>
                          </td>
                          <td className="py-2 px-3 text-slate-500">{g.fy || "–"}</td>
                          <td className="py-2 px-3 text-right font-medium text-slate-800">{fmtCr(g.amount_cr)}</td>
                          <td className="py-2 px-3 text-slate-600 max-w-[200px] truncate italic" title={g.raw_geography}>{g.raw_geography || "Empty"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="h-20 flex items-center justify-center text-slate-600 text-sm">Loading geography breakdown…</div>
        )}
      </SectionHeader>

      {/* Multi-State Grant Reconciliation */}
      <SectionHeader
        title="Multi-State Grant Reconciliation"
        subtitle={multiState?.summary ? `${multiState.summary.total_multi_state_grants} grants · ${fmtCr(multiState.summary.total_affected_allocation_cr)} affected allocation` : "Loading…"}
        data-testid="multi-state-section"
        headerRight={
          <button
            onClick={() => {
              axios.get(`${API}/data-health/multi-state`, { params: { page: 1, per_page: 10000 } })
                .then(r => exportToCSV(r.data.data, 'multi_state_reconciliation', [
                  { label: 'Grant ID', key: 'grant_id' },
                  { label: 'Grantee', key: 'grantee' },
                  { label: 'Theme', key: 'theme' },
                  { label: 'Total Allocation (Cr)', key: 'total_allocation_cr' },
                  { label: 'States', key: 'n_states' },
                  { label: 'State', key: 'state' },
                  { label: 'Districts', key: 'district_count' },
                  { label: 'Blocks', key: 'block_count' },
                  { label: 'Method', key: 'method' },
                  { label: 'Source', key: 'source' },
                  { label: 'Share %', key: 'share_pct' },
                  { label: 'Est. Allocation (Cr)', key: 'estimated_allocation_cr' },
                  { label: 'Verify', key: 'verify' },
                ]));
            }}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 transition-all"
          >
            <Download size={12} /> Export CSV
          </button>
        }
      >
        {/* Summary pills */}
        {multiState?.summary && (
          <div className="flex flex-wrap gap-2 mb-4">
            {Object.entries(multiState.summary.by_method || {}).map(([method, count]) => (
              <span key={method} className={`text-xs px-2.5 py-1 rounded-full font-medium ${METHOD_COLOR[method] || "bg-slate-100 text-slate-600"}`}>
                {count} grants — {method}
              </span>
            ))}
            {Object.entries(multiState.summary.by_source || {}).map(([src, count]) => (
              count > 0 && (
                <span
                  key={src}
                  className={`text-xs px-2.5 py-1 rounded-full font-medium inline-flex items-center gap-1.5 ${src === 'Override' ? 'bg-amber-100 text-amber-800' : 'bg-emerald-50 text-emerald-700'}`}
                  title={src === 'Override' ? 'Geography was modified by a hardcoded override list' : 'Geography is from Flux (Snowflake) as-is'}
                >
                  <span className={`w-1.5 h-1.5 rounded-full ${src === 'Override' ? 'bg-amber-500' : 'bg-emerald-500'}`} />
                  {count} grants — {src}
                </span>
              )
            ))}
            <div className="flex items-center gap-1 text-xs text-slate-600 ml-auto">
              <Info size={11} />
              Proportional apportionment: block count → district count → equal split
            </div>
          </div>
        )}

        {/* Filter bar */}
        <div className="flex gap-2 mb-3">
          <input
            type="text"
            placeholder="Search by grant ID or grantee…"
            value={msFilter}
            onChange={e => { setMsFilter(e.target.value); setMsPage(1); }}
            className="text-xs border border-slate-200 rounded-lg px-3 py-1.5 flex-1 focus:outline-none focus:ring-1 focus:ring-[#B71C1C]"
          />
        </div>

        {/* Reconciliation Table */}
        <div className="overflow-x-auto -mx-5 px-5 sm:mx-0 sm:px-0">
          <table className="w-full text-xs min-w-[1100px]" data-testid="multi-state-table">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50">
                <th className="text-left font-medium text-slate-500 uppercase py-2 px-2 whitespace-nowrap">Grant ID</th>
                <th className="text-left font-medium text-slate-500 uppercase py-2 whitespace-nowrap">Grantee</th>
                <th className="text-left font-medium text-slate-500 uppercase py-2 whitespace-nowrap">Theme</th>
                <th className="text-right font-medium text-slate-500 uppercase py-2 whitespace-nowrap">Total Alloc</th>
                <th className="text-center font-medium text-slate-500 uppercase py-2 whitespace-nowrap">States</th>
                <th className="text-left font-medium text-slate-500 uppercase py-2 whitespace-nowrap">State</th>
                <th className="text-right font-medium text-slate-500 uppercase py-2 whitespace-nowrap">Districts</th>
                <th className="text-right font-medium text-slate-500 uppercase py-2 whitespace-nowrap">Blocks</th>
                <th className="text-left font-medium text-slate-500 uppercase py-2 whitespace-nowrap">Method</th>
                <th className="text-center font-medium text-slate-500 uppercase py-2 whitespace-nowrap">Source</th>
                <th className="text-right font-medium text-slate-500 uppercase py-2 whitespace-nowrap">Share %</th>
                <th className="text-right font-medium text-slate-500 uppercase py-2 whitespace-nowrap">Est. Alloc</th>
                <th className="text-center font-medium text-slate-500 uppercase py-2 whitespace-nowrap">Check</th>
              </tr>
            </thead>
            <tbody>
              {msLoading && (
                <tr><td colSpan={13} className="text-center py-8 text-slate-600">Loading reconciliation data…</td></tr>
              )}
              {!msLoading && (multiState?.data || []).map((r, i, arr) => {
                  const isFirstOfGrant = i === 0 || arr[i-1].grant_id !== r.grant_id;
                  return (
                    <tr key={i} className={`border-b border-slate-50 ${isFirstOfGrant ? "bg-white" : "bg-slate-50/40"} hover:bg-blue-50/30 transition-colors`}>
                      <td className="py-2 px-2 font-mono text-slate-600 whitespace-nowrap">{isFirstOfGrant ? r.grant_id : ""}</td>
                      <td className="py-2 text-slate-700 max-w-[140px] truncate">{isFirstOfGrant ? r.grantee : ""}</td>
                      <td className="py-2">{isFirstOfGrant ? <ThemeChip theme={r.theme} /> : ""}</td>
                      <td className="py-2 text-right font-medium text-[#B71C1C] whitespace-nowrap">{isFirstOfGrant ? fmtCr(r.total_allocation_cr) : ""}</td>
                      <td className="py-2 text-center text-slate-500">{isFirstOfGrant ? r.n_states : ""}</td>
                      <td className="py-2 font-medium text-slate-800 whitespace-nowrap">{r.state}</td>
                      <td className="py-2 text-right text-slate-500">{r.district_count}</td>
                      <td className="py-2 text-right text-slate-500">{r.block_count}</td>
                      <td className="py-2">
                        <span className={`px-1.5 py-0.5 rounded text-xs whitespace-nowrap ${METHOD_COLOR[r.method] || "bg-slate-100 text-slate-600"}`}>{r.method}</span>
                      </td>
                      <td className="py-2 text-center">
                        {isFirstOfGrant && (
                          <span
                            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium whitespace-nowrap ${r.source === 'Override' ? 'bg-amber-100 text-amber-800' : 'bg-emerald-50 text-emerald-700'}`}
                            title={r.source === 'Override' ? 'Hardcoded override applied to this grant\u2019s geography (see Logics page)' : 'Sourced directly from Flux (Snowflake)'}
                          >
                            <span className={`w-1.5 h-1.5 rounded-full ${r.source === 'Override' ? 'bg-amber-500' : 'bg-emerald-500'}`} />
                            {r.source}
                          </span>
                        )}
                      </td>
                      <td className="py-2 text-right font-medium">{r.share_pct}%</td>
                      <td className="py-2 text-right font-semibold text-slate-800">{fmtCr(r.estimated_allocation_cr)}</td>
                      <td className="py-2 text-center">
                        <span className={r.verify === 'Match' ? "text-emerald-600" : "text-red-600"}>{r.verify === 'Match' ? '✓' : '✗'}</span>
                      </td>
                    </tr>
                  );
                })}
              {!msLoading && !sf && (
                <tr><td colSpan={13} className="text-center py-8 text-slate-600">Requires FLUXX data connection</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {multiState && multiState.total > 30 && (
          <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100">
            <span className="text-xs text-slate-600">
              Showing {((msPage-1)*30)+1}–{Math.min(msPage*30, multiState.total)} of {multiState.total} state rows
            </span>
            <div className="flex gap-2">
              <button onClick={() => setMsPage(p => Math.max(1, p-1))} disabled={msPage === 1}
                className="text-xs px-3 py-1.5 bg-slate-100 rounded-lg disabled:opacity-40 hover:bg-slate-200 transition-colors">
                Prev
              </button>
              <button onClick={() => setMsPage(p => p+1)} disabled={msPage * 30 >= multiState.total}
                className="text-xs px-3 py-1.5 bg-slate-100 rounded-lg disabled:opacity-40 hover:bg-slate-200 transition-colors">
                Next
              </button>
            </div>
          </div>
        )}

        <div className="mt-3 text-xs text-slate-600 flex items-start gap-1.5">
          <Info size={11} className="mt-0.5 flex-shrink-0" />
          * "Proportional Estimate" — allocation for multi-state grants distributed by block count (where available), district count (fallback), or equal split (final fallback). Verify column confirms state-row allocations sum to total grant allocation.
          The <strong className="text-emerald-700">Source</strong> column shows whether the grant's geography came directly from Flux (Snowflake) or was modified via a hardcoded override (see <a href="/logics" className="underline text-[#B71C1C] hover:text-[#8B1515]">Logics</a> page for the full override list).
        </div>
      </SectionHeader>

      {/* Data Gap Summary by Theme */}
      {gapSummary.length > 0 && (
        <SectionHeader title="Data Gap Summary by Theme" subtitle="Average mandatory field completeness across 7 key fields — sorted worst to best">
          <div className="space-y-3" data-testid="gap-summary-section">
            {gapSummary.map((r, i) => {
              const color = r.avg_completeness >= 95 ? "bg-green-500" : r.avg_completeness >= 80 ? "bg-amber-400" : "bg-red-500";
              const textColor = r.avg_completeness >= 95 ? "text-green-700" : r.avg_completeness >= 80 ? "text-amber-700" : "text-red-600";
              const missingField = Object.entries(r)
                .filter(([k]) => !["theme","total_grants","avg_completeness"].includes(k))
                .sort(([,a],[,b]) => a - b)[0];
              return (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-36 flex-shrink-0 text-right">
                    <ThemeChip theme={r.theme} />
                  </div>
                  <div className="flex-1 relative h-5 bg-slate-100 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full ${color} transition-all`} style={{ width: `${r.avg_completeness}%` }} />
                  </div>
                  <div className={`text-xs font-bold w-12 text-right flex-shrink-0 ${textColor}`}>
                    {r.avg_completeness}%
                  </div>
                  {missingField && missingField[1] < 100 && (
                    <div className="text-xs text-slate-600 w-40 flex-shrink-0 truncate">
                      Gap: <span className="text-amber-600 font-medium">{missingField[0]}</span> ({missingField[1]}%)
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <div className="mt-4 pt-3 border-t border-slate-100 flex gap-4 text-xs">
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-green-500 inline-block" /> ≥95% Complete</span>
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-amber-400 inline-block" /> 80–94% Complete</span>
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-red-500 inline-block" /> &lt;80% Complete</span>
          </div>
        </SectionHeader>
      )}

      {/* Aspirational Districts Table */}
      <SectionHeader 
        title="Aspirational Districts" 
        subtitle={`NITI Aayog Aspirational Districts Programme — ${aspirationalLists?.districts?.length || 0} districts across India`} 
        collapsible 
        defaultOpen={false}
        headerRight={
          <button
            onClick={() => exportToCSV(aspirationalLists?.districts || [], 'aspirational_districts', [
              { label: 'State', key: 'state' },
              { label: 'District', key: 'district' },
              { label: 'Grant Coverage', getValue: r => r.has_grant ? 'Yes' : 'No' },
            ])}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 transition-all"
          >
            <Download size={12} /> Export CSV
          </button>
        }
      >
        <div className="mb-3 flex items-center gap-2">
          <div className="relative flex-1 max-w-xs">
            <Search size={13} className="absolute left-2.5 top-2.5 text-slate-600" />
            <input
              type="text"
              placeholder="Search district or state..."
              value={adSearch}
              onChange={e => setAdSearch(e.target.value)}
              className="w-full pl-8 pr-3 py-2 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              data-testid="aspirational-districts-search"
            />
          </div>
          <span className="text-xs text-slate-600">{filteredDistricts.length} results</span>
        </div>
        <div className="overflow-auto max-h-72 border border-slate-100 rounded-lg">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-3 py-2 font-medium text-slate-600">#</th>
                <th className="text-left px-3 py-2 font-medium text-slate-600">District</th>
                <th className="text-left px-3 py-2 font-medium text-slate-600">State</th>
                <th className="text-center px-3 py-2 font-medium text-slate-600" title="Has at least one active grant in this district">Grant Coverage</th>
              </tr>
            </thead>
            <tbody>
              {filteredDistricts.map((d, i) => (
                <tr key={i} className="border-b border-slate-50 hover:bg-amber-50/40 transition-colors">
                  <td className="px-3 py-1.5 text-slate-600">{i + 1}</td>
                  <td className="px-3 py-1.5 font-medium text-slate-800">{d.district}</td>
                  <td className="px-3 py-1.5 text-slate-600">{d.state}</td>
                  <td className="px-3 py-1.5 text-center" data-testid={`district-grant-coverage-${i}`}>
                    {d.has_grant
                      ? <span className="text-green-600 font-bold" title="Grant present">✓</span>
                      : <span className="text-red-400" title="No grant">✗</span>
                    }
                  </td>
                </tr>
              ))}
              {filteredDistricts.length === 0 && (
                <tr><td colSpan={4} className="px-3 py-4 text-center text-slate-600">No results</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </SectionHeader>

      {/* Aspirational Blocks Table */}
      <SectionHeader
        title="Aspirational Blocks"
        subtitle={
          <span className="flex items-center gap-2 flex-wrap">
            <span>{aspirationalLists?.blocks?.length || 0} blocks across India</span>
            <span className="text-slate-500">·</span>
            <a
              href="https://abp.championsofchange.gov.in/assets/uploads/LIST+OF+ASPIRATIONAL+BLOCKS+_V1.0.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 font-medium"
              data-testid="niti-aayog-blocks-pdf-link"
            >
              Source: NITI Aayog Aspirational Blocks Programme V1.0
              <ExternalLink size={11} />
            </a>
          </span>
        }
        collapsible
        defaultOpen={false}
        headerRight={
          <button
            onClick={() => exportToCSV(aspirationalLists?.blocks || [], 'aspirational_blocks', [
              { label: 'State', key: 'state' },
              { label: 'District', key: 'district' },
              { label: 'Block', key: 'block' },
              { label: 'Grant Coverage', getValue: r => r.has_grant ? 'Yes' : 'No' },
            ])}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 transition-all"
          >
            <Download size={12} /> Export CSV
          </button>
        }
      >
        <div className="mb-3 flex items-center gap-2">
          <div className="relative flex-1 max-w-xs">
            <Search size={13} className="absolute left-2.5 top-2.5 text-slate-600" />
            <input
              type="text"
              placeholder="Search block, district, or state..."
              value={abSearch}
              onChange={e => setAbSearch(e.target.value)}
              className="w-full pl-8 pr-3 py-2 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              data-testid="aspirational-blocks-search"
            />
          </div>
          <span className="text-xs text-slate-600">{filteredBlocks.length} results</span>
        </div>
        <div className="overflow-auto max-h-72 border border-slate-100 rounded-lg">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-3 py-2 font-medium text-slate-600">#</th>
                <th className="text-left px-3 py-2 font-medium text-slate-600">Block</th>
                <th className="text-left px-3 py-2 font-medium text-slate-600">District</th>
                <th className="text-left px-3 py-2 font-medium text-slate-600">State</th>
                <th className="text-center px-3 py-2 font-medium text-slate-600" title="Has at least one active grant in this block">Grant Coverage</th>
              </tr>
            </thead>
            <tbody>
              {filteredBlocks.map((b, i) => (
                <tr key={i} className="border-b border-slate-50 hover:bg-amber-50/40 transition-colors">
                  <td className="px-3 py-1.5 text-slate-600">{i + 1}</td>
                  <td className="px-3 py-1.5 font-medium text-amber-700">{b.block}</td>
                  <td className="px-3 py-1.5 text-slate-600">{b.district}</td>
                  <td className="px-3 py-1.5 text-slate-600">{b.state}</td>
                  <td className="px-3 py-1.5 text-center" data-testid={`block-grant-coverage-${i}`}>
                    {b.has_grant
                      ? <span className="text-green-600 font-bold" title="Grant present">✓</span>
                      : <span className="text-red-400" title="No grant">✗</span>
                    }
                  </td>
                </tr>
              ))}
              {filteredBlocks.length === 0 && (
                <tr><td colSpan={5} className="px-3 py-4 text-center text-slate-600">No results</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </SectionHeader>

      {/* Early Payments — Paid before Project Start Date */}
      <SectionHeader
        title={`Early Payments — Paid Before Start Date${earlyPayments ? ` (${earlyPayments.total})` : ''}`}
        subtitle="Grants where payment was disbursed before the project start date"
        collapsible
        defaultOpen={true}
      >
        {earlyPayments ? (
          earlyPayments.total > 0 ? (
            <div data-testid="early-payments-section">
              <div className="flex items-center gap-3 mb-3">
                <div className="relative flex-1 max-w-xs">
                  <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-600" />
                  <input
                    data-testid="early-payments-search"
                    className="w-full pl-8 pr-3 py-1.5 border rounded-md text-xs focus:outline-none focus:ring-1 focus:ring-blue-400"
                    placeholder="Search by grant ID, grantee, title..."
                    value={epSearch}
                    onChange={e => setEpSearch(e.target.value)}
                  />
                </div>
                <button
                  data-testid="early-payments-export"
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-md transition-colors"
                  onClick={() => exportToCSV(earlyPayments.data, 'early_payments', [
                    { label: 'Grant ID', key: 'grant_id' },
                    { label: 'Grantee', key: 'grantee_name' },
                    { label: 'Project Title', key: 'project_title' },
                    { label: 'Theme', key: 'theme' },
                    { label: 'Status', key: 'status' },
                    { label: 'Grant Approved Date', key: 'grant_approved_date' },
                    { label: 'Project Start Date', key: 'project_start_date' },
                    { label: 'Paid Date', key: 'paid_date' },
                    { label: 'Amount Paid', getValue: r => r.amount_paid },
                    { label: 'Days Before Start', key: 'days_before' },
                  ])}
                >
                  <Download size={12} /> Export CSV
                </button>
              </div>
              <div className="overflow-x-auto border rounded-lg">
                <table className="min-w-full text-xs" data-testid="early-payments-table">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left font-semibold text-slate-600">Grant ID</th>
                      <th className="px-3 py-2 text-left font-semibold text-slate-600">Grantee</th>
                      <th className="px-3 py-2 text-left font-semibold text-slate-600">Project Title</th>
                      <th className="px-3 py-2 text-left font-semibold text-slate-600">Theme</th>
                      <th className="px-3 py-2 text-left font-semibold text-slate-600">Grant Approved</th>
                      <th className="px-3 py-2 text-left font-semibold text-slate-600">Project Start</th>
                      <th className="px-3 py-2 text-left font-semibold text-slate-600">Paid Date</th>
                      <th className="px-3 py-2 text-right font-semibold text-slate-600">Amount Paid</th>
                      <th className="px-3 py-2 text-right font-semibold text-slate-600">Days Before</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {earlyPayments.data
                      .filter(r => {
                        if (!epSearch) return true;
                        const q = epSearch.toLowerCase();
                        return (r.grant_id || '').toLowerCase().includes(q)
                          || (r.grantee_name || '').toLowerCase().includes(q)
                          || (r.project_title || '').toLowerCase().includes(q);
                      })
                      .map((r, i) => (
                        <tr key={i} className="hover:bg-amber-50/50 transition-colors" data-testid={`early-payment-row-${i}`}>
                          <td className="px-3 py-2 font-mono text-blue-700 whitespace-nowrap">{r.grant_id}</td>
                          <td className="px-3 py-2 max-w-[200px] truncate" title={r.grantee_name}>{r.grantee_name}</td>
                          <td className="px-3 py-2 max-w-[220px] truncate" title={r.project_title}>{r.project_title}</td>
                          <td className="px-3 py-2 whitespace-nowrap"><ThemeChip theme={r.theme} /></td>
                          <td className="px-3 py-2 whitespace-nowrap">{fmtDate(r.grant_approved_date)}</td>
                          <td className="px-3 py-2 whitespace-nowrap">{fmtDate(r.project_start_date)}</td>
                          <td className="px-3 py-2 whitespace-nowrap text-red-600 font-medium">{fmtDate(r.paid_date)}</td>
                          <td className="px-3 py-2 text-right whitespace-nowrap">{fmtCr(r.amount_paid / 1e7)}</td>
                          <td className="px-3 py-2 text-right whitespace-nowrap">
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-medium">
                              {r.days_before}d
                            </span>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-green-500">
              <CheckCircle size={24} className="mx-auto mb-2" />
              <div className="text-sm">No early payments found — all payments are on or after the project start date</div>
            </div>
          )
        ) : (
          <div className="h-20 flex items-center justify-center">
            <RefreshCw className="animate-spin text-slate-600" size={20} />
          </div>
        )}
      </SectionHeader>

      {/* Data Validation Report Section */}
      <SectionHeader 
        title="Data Validation Report" 
        subtitle="Post-ETL validation: Unmapped values that need attention"
        collapsible 
        defaultOpen={false}
      >
        {validationLoading ? (
          <div className="h-32 flex items-center justify-center">
            <RefreshCw className="animate-spin text-slate-600" size={24} />
          </div>
        ) : validationReport ? (
          <div className="space-y-4">
            {/* Health Status Banner */}
            <div className={`rounded-lg p-4 flex items-start gap-3 ${
              validationReport.health_status === "healthy" ? "bg-green-50 border border-green-200" :
              validationReport.health_status === "minor_issues" ? "bg-amber-50 border border-amber-200" :
              "bg-red-50 border border-red-200"
            }`}>
              {validationReport.health_status === "healthy" ? (
                <CheckCircle className="text-green-600 flex-shrink-0" size={20} />
              ) : validationReport.health_status === "minor_issues" ? (
                <AlertCircle className="text-amber-600 flex-shrink-0" size={20} />
              ) : (
                <AlertCircle className="text-red-600 flex-shrink-0" size={20} />
              )}
              <div>
                <div className={`font-medium ${
                  validationReport.health_status === "healthy" ? "text-green-800" :
                  validationReport.health_status === "minor_issues" ? "text-amber-800" :
                  "text-red-800"
                }`}>
                  {validationReport.health_status === "healthy" ? "All Data Mapped" :
                   validationReport.health_status === "minor_issues" ? "Minor Issues Found" :
                   "Needs Attention"}
                </div>
                <div className={`text-sm ${
                  validationReport.health_status === "healthy" ? "text-green-700" :
                  validationReport.health_status === "minor_issues" ? "text-amber-700" :
                  "text-red-700"
                }`}>
                  {validationReport.health_message}
                </div>
              </div>
            </div>

            {/* Summary Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <div className="bg-slate-50 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-slate-800">{validationReport.summary.total_districts_in_db}</div>
                <div className="text-xs text-slate-500">Districts</div>
                {validationReport.summary.unmapped_districts_count > 0 && (
                  <div className="text-xs text-amber-600 mt-1">{validationReport.summary.unmapped_districts_count} need review</div>
                )}
              </div>
              <div className="bg-slate-50 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-slate-800">{validationReport.summary.total_blocks_in_db}</div>
                <div className="text-xs text-slate-500">Blocks</div>
                {validationReport.summary.unmapped_blocks_count > 0 && (
                  <div className="text-xs text-amber-600 mt-1">{validationReport.summary.unmapped_blocks_count} flagged</div>
                )}
              </div>
              <div className="bg-slate-50 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-slate-800">{validationReport.summary.total_states_in_db}</div>
                <div className="text-xs text-slate-500">States</div>
                {validationReport.summary.unmapped_states_count > 0 && (
                  <div className="text-xs text-amber-600 mt-1">{validationReport.summary.unmapped_states_count} unmapped</div>
                )}
              </div>
              <div className="bg-slate-50 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-slate-800">{validationReport.summary.total_themes_in_db}</div>
                <div className="text-xs text-slate-500">Themes</div>
                {validationReport.summary.new_themes_count > 0 && (
                  <div className="text-xs text-purple-600 mt-1">{validationReport.summary.new_themes_count} new (auto-color)</div>
                )}
              </div>
              <div className="bg-slate-50 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-slate-800">{validationReport.summary.total_statuses_in_db}</div>
                <div className="text-xs text-slate-500">Statuses</div>
                {validationReport.summary.new_statuses_count > 0 && (
                  <div className="text-xs text-purple-600 mt-1">{validationReport.summary.new_statuses_count} new (auto-color)</div>
                )}
              </div>
            </div>

            {/* Unmapped States */}
            {validationReport.unmapped_states?.length > 0 && (
              <div className="border border-slate-200 rounded-lg p-3">
                <div className="font-medium text-sm text-slate-700 mb-2">Unmapped States (will show "Other" zone)</div>
                <div className="flex flex-wrap gap-2">
                  {validationReport.unmapped_states.map((s, i) => (
                    <span key={i} className="text-xs bg-amber-100 text-amber-800 px-2 py-1 rounded">
                      {s.state}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* New Themes */}
            {validationReport.new_themes?.length > 0 && (
              <div className="border border-slate-200 rounded-lg p-3">
                <div className="font-medium text-sm text-slate-700 mb-2">New Themes (auto-generated colors)</div>
                <div className="flex flex-wrap gap-2">
                  {validationReport.new_themes.map((t, i) => (
                    <span key={i} className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded">
                      {displayTheme(t.theme)}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* New Statuses */}
            {validationReport.new_statuses?.length > 0 && (
              <div className="border border-slate-200 rounded-lg p-3">
                <div className="font-medium text-sm text-slate-700 mb-2">New Payment Statuses (auto-generated colors)</div>
                <div className="flex flex-wrap gap-2">
                  {validationReport.new_statuses.map((s, i) => (
                    <span key={i} className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded">
                      {s.status}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Unmapped Districts */}
            {validationReport.unmapped_districts?.length > 0 && (
              <div className="border border-slate-200 rounded-lg p-3">
                <div className="font-medium text-sm text-slate-700 mb-2">Districts Needing Review (may not appear on map)</div>
                <div className="overflow-auto max-h-48">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="text-left px-2 py-1">District</th>
                        <th className="text-left px-2 py-1">State</th>
                        <th className="text-left px-2 py-1">Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {validationReport.unmapped_districts.slice(0, 20).map((d, i) => (
                        <tr key={i} className="border-t border-slate-100">
                          <td className="px-2 py-1 font-medium">{d.district}</td>
                          <td className="px-2 py-1 text-slate-600">{d.state}</td>
                          <td className="px-2 py-1 text-slate-500">{d.reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {validationReport.unmapped_districts.length > 20 && (
                    <div className="text-xs text-slate-600 text-center py-2">
                      ... and {validationReport.unmapped_districts.length - 20} more
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* All Clear Message */}
            {validationReport.health_status === "healthy" && (
              <div className="text-center py-4 text-green-600">
                <CheckCircle size={32} className="mx-auto mb-2" />
                <div className="text-sm">All values are properly mapped!</div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-8 text-slate-600">
            <AlertCircle size={24} className="mx-auto mb-2" />
            <div className="text-sm">Could not load validation report</div>
          </div>
        )}
      </SectionHeader>
    </div>
  );
}
